import { MouseEvent, useEffect, useState } from 'react';

import { DataEventProps } from '@/libraries/wpl/foundations/src/data-event-props';

import { ProductKeys } from '@atlassiansox/asc-stargate';

import { useProductTemplateContext } from '@/components/product-template-context-provider';
import { useSiteSelectorContext } from '@/components/site-selector-modal/site-selector-context';
import { getTemplateAction } from '@/components/site-selector-modal/template-action-utils';
import { useSitesWithCurrentProduct } from '@/components/user-context/helpers/cohorts';
import {
	Button,
	ButtonProps,
	ResponsiveButtonSizes,
} from '@/libraries/design/components/button/src/button.tsx';
import { Link } from '@/libraries/design/components/link/src/link.tsx';
import { useLocale } from '@/libraries/web-framework/src/localization/use-locale';
import {
	getLocalizedHref,
	useLocalizedHref,
} from '@/libraries/web-framework/src/localization/use-localized-href';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

export interface UniformTemplateModalButtonProps {
	text: string;
	productKey: string;
	templateName: string;
	pluginModuleKey?: string;
	href?: string;
	responsiveSize?: ResponsiveButtonSizes;
	autoModalOpen?: boolean;
	type?: 'button' | 'link';
}

const UniformTemplateModalButton = ({
	text,
	productKey,
	templateName,
	pluginModuleKey,
	href,
	responsiveSize,
	autoModalOpen = false,
	type = 'button',
}: UniformTemplateModalButtonProps) => {
	const { openModalForTemplate } = useSiteSelectorContext();
	const { setTemplate } = useProductTemplateContext();
	// AIS = Atlassian Identity Service - provides user's existing Atlassian sites
	const aisSites = useSitesWithCurrentProduct();
	const locale = useLocale();
	const localizedSignupHref = useLocalizedHref('/try/cloud/signup');
	const defaultHref = getDefaultHref({ productKey, locale });

	const [buttonProps, setButtonProps] = useState<ButtonProps>({
		href: defaultHref,
		responsiveSize: responsiveSize,
	});
	const fullStargateProductKey =
		productKey === 'jira-service-management'
			? 'jira-servicedesk.ondemand'
			: productKey === 'jira-product-discovery'
				? productKey
				: `${productKey}.ondemand`;

	useEffect(() => {
		if (pluginModuleKey && productKey) {
			const templateData = {
				templateId: pluginModuleKey,
				templateName,
				productKey: fullStargateProductKey as ProductKeys,
			};

			setTemplate(templateData);

			// Get site URLs for the current product
			const siteUrls = aisSites?.map((site) => site.url) ?? [];

			// Determine action based on user's sites
			const action = getTemplateAction({
				templateData,
				siteUrls,
				localizedSignupHref,
			});

			if (action.type === 'modal') {
				setButtonProps({
					onClick: (event: MouseEvent<HTMLButtonElement>) => {
						event.stopPropagation();
						event.preventDefault();
						openModalForTemplate(templateData);
					},
				});
			} else {
				setButtonProps({
					href: action.href,
				});
			}
		} else if (href) {
			setButtonProps({
				href: getLocalizedHref({ href, locale }),
			});
		}

		return () => {
			setTemplate(null);
		};
	}, [
		openModalForTemplate,
		templateName,
		pluginModuleKey,
		fullStargateProductKey,
		href,
		productKey,
		locale,
		aisSites,
		localizedSignupHref,
		setTemplate,
	]);

	useEffect(() => {
		if (!checkFeatureFlagGate(featureFlags.jpdWacRedirectTemplateAfterLogin)) {
			return;
		}

		if (!autoModalOpen || !pluginModuleKey || !aisSites || aisSites.length === 0) {
			return;
		}

		const urlParams = new URLSearchParams(location.search);
		const tryTemplate = urlParams.get('tryTemplate');

		if (tryTemplate !== pluginModuleKey) {
			return;
		}

		openModalForTemplate({
			templateId: pluginModuleKey,
			templateName,
			productKey: fullStargateProductKey as ProductKeys,
		});
	}, [
		autoModalOpen,
		pluginModuleKey,
		aisSites,
		openModalForTemplate,
		templateName,
		fullStargateProductKey,
	]);

	const dataEventProps: DataEventProps = {
		'data-product-key': 'uniform',
		'data-event': 'clicked',
		'data-event-component': 'linkButton',
		'data-event-container': 'modal',
	};

	return type === 'link' ? (
		<Link
			href={('href' in buttonProps && buttonProps.href) || defaultHref}
			text={text}
			dataEventProps={dataEventProps}
			aria-label={text}
		/>
	) : (
		<Button {...buttonProps} dataEventProps={dataEventProps}>
			{text}
		</Button>
	);
};

interface GetDefaultHrefArgs {
	productKey: string;
	locale: string;
}
function getDefaultHref(args: GetDefaultHrefArgs) {
	const { locale, productKey } = args;

	if (productKey === 'jira-software') {
		return getLocalizedHref({
			href: '/try/cloud/signup?bundle=jira&edition=free',
			locale,
		});
	}
	if (productKey === 'confluence') {
		return getLocalizedHref({
			href: '/try/cloud/signup?bundle=confluence&edition=free',
			locale,
		});
	}
	if (productKey === 'jira-service-management') {
		return getLocalizedHref({
			href: '/try/cloud/signup?bundle=jira-service-management&edition=free',
			locale,
		});
	}
	if (productKey === 'jira-product-discovery') {
		return getLocalizedHref({
			href: '/try/cloud/signup?bundle=jira-product-discovery&edition=free',
			locale,
		});
	}

	return '';
}

export default UniformTemplateModalButton;
